import { Box, Typography, makeStyles } from "@material-ui/core";
import { ButtonBase } from "@material-ui/core";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import * as React from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  SimulationConfig,
  SimulationConfigMarketPriceChangeAtBoughtModeEnum,
  SimulationConfigWithIdProposedByEnum,
  SimulationPatternEnum,
} from "@/api/generated";
import { layoutTokens as layout, semanticColors as colors, textStyles } from "@/designTokens";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import { PropertyManagementDetailSnackbarState, useRequestInitialLoading } from "@/view_models/atoms";
import { SelectedProperty } from "@/view_models/propertySelectors";

import { DetailLink } from "./DetailSection";
import {
  SimulationConfigNeutral,
  SimulationConfigOptimistic,
  SimulationConfigPessimistic,
  SimulationConfigDefaultType,
} from "./simulationSettingsConstants";

const useStyles = makeStyles({
  root: { padding: layout.space[24], background: colors["surface/card"], borderRadius: layout.radius.lg },
  title: { ...textStyles["SP/Heading/H2"], marginBottom: layout.space[24] },
  choice: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: layout.space[16],
    padding: "20px 0",
    textAlign: "left",
    borderBottom: `1px solid ${colors["border/default"]}`,
  },
  label: { ...textStyles["SP/Heading/H3"] },
  description: { ...textStyles["SP/Body/Small"], color: colors["text/secondary"], marginTop: layout.space[8] },
});
// 楽観的
const SimulationConfigOptionOptimistic = SimulationPatternEnum.楽観的;
// 中立
const SimulationConfigOptionNeutral = SimulationPatternEnum.中立;
// 悲観的
const SimulationConfigOptionPessimistic = SimulationPatternEnum.悲観的;
// カスタム
const SimulationConfigOptionCustom = SimulationPatternEnum.カスタム;

const SimulationConfigOptions = [
  {
    label: SimulationConfigOptionOptimistic,
    value: SimulationConfigOptionOptimistic,
  },
  {
    label: SimulationConfigOptionNeutral,
    value: SimulationConfigOptionNeutral,
  },
  {
    label: SimulationConfigOptionPessimistic,
    value: SimulationConfigOptionPessimistic,
  },
  {
    label: SimulationConfigOptionCustom,
    value: SimulationConfigOptionCustom,
  },
];

export const useSimulationSettings = () => {
  const selected_property = useRecoilValue(SelectedProperty);
  const [simulationPattern, setSimulationPattern] = React.useState<SimulationPatternEnum>(
    selected_property.simulation_configs[0]?.simulation_pattern ?? SimulationConfigOptionOptimistic
  );
  const setManagementDetailSnackBarState = useSetRecoilState(PropertyManagementDetailSnackbarState);
  const requestInitialLoading = useRequestInitialLoading();
  const getSimulationConfigBySelector = (simPattern: SimulationPatternEnum): SimulationConfig => {
    let selectedConfig: SimulationConfigDefaultType;
    switch (simPattern) {
      case SimulationConfigOptionOptimistic:
        selectedConfig = { ...SimulationConfigOptimistic };
        break;
      case SimulationConfigOptionNeutral:
        selectedConfig = { ...SimulationConfigNeutral };
        break;
      case SimulationConfigOptionPessimistic:
        selectedConfig = { ...SimulationConfigPessimistic };
        break;
      default:
        selectedConfig = { ...SimulationConfigNeutral };
    }
    // market_price_change_down_valueの正負
    if (selectedConfig.market_price_mode_sign === "positive") {
      selectedConfig.market_price_change_down_value *= -1;
    }
    //

    return {
      // 価格
      market_price_change_down_value: selectedConfig.market_price_change_down_value,
      market_price_mode: selectedConfig.market_price_mode,
      market_price_change_ends_at: selectedConfig.market_price_change_ends_at,
      // 収支
      house_rent_spare_months_for_N_contracts: selectedConfig.house_rent_spare_months_for_N_contracts,
      house_rent_spare_months: selectedConfig.house_rent_spare_months,
      house_rent_price_change_down_value: selectedConfig.house_rent_price_change_down_value,
      house_rent_mode: selectedConfig.house_rent_mode,
      // 初期値
      market_price_change_at_bought_value: 0,
      house_rent_price_change_ends_at: 100, // 家賃変動率 N回目の契約から変動ストップ のNの値
      market_price_change_at_bought_mode: SimulationConfigMarketPriceChangeAtBoughtModeEnum.Percentage,
      // 選択パターン
      simulation_pattern: simPattern,
    };
  };
  const onClickSave = async (selectedSimPattern: SimulationPatternEnum) => {
    if (selectedSimPattern === simulationPattern) {
      return;
    }
    setSimulationPattern(selectedSimPattern);

    if (selectedSimPattern === SimulationConfigOptionCustom) {
      return;
    }
    // シミュレーションパラメータを送信
    const fp = PropallyApiFactory();

    // 既存のシミュレーション設定を取得
    const user_simconf = selected_property.simulation_configs.find((c) => {
      return c.proposed_by !== SimulationConfigWithIdProposedByEnum.Realtor;
    });

    // シミュレーション設定を取得
    const selectedSimConf = getSimulationConfigBySelector(selectedSimPattern);
    try {
      const data = await fp.v1PropertiesPropertyIdSimulationConfigsSimulationConfigIdPatch(
        selected_property.id,
        user_simconf.id,
        selectedSimConf,
        {
          withCredentials: true,
        }
      );
      if (data.status === 200) {
        requestInitialLoading();
      }
    } catch (e) {
      console.error(e);
      setManagementDetailSnackBarState({
        message: "シミュレーション設定を変更できませんでした",
        status: "error",
        isOpen: true,
      });
    }
  };

  const simulationConfigDescriptions = React.useMemo(
    () =>
      new Map<SimulationPatternEnum, { price: string; vacancyPeriod: string; rentChange: string }>([
        [
          SimulationConfigOptionOptimistic,
          {
            price: "毎年【1】％上昇",
            vacancyPeriod: "【3】回の賃貸借契約ごとに【1】ヶ月発生",
            rentChange: "賃貸借契約ごとに現在より【3】%上昇",
          },
        ],
        [
          SimulationConfigOptionNeutral,
          {
            price: "変わらない",
            vacancyPeriod: "【2】回の賃貸借契約ごとに【1】ヶ月発生",
            rentChange: "賃貸借契約ごとに現在より変わらない",
          },
        ],
        [
          SimulationConfigOptionPessimistic,
          {
            price: "毎年【1】％下落",
            vacancyPeriod: "【1】回の賃貸借契約ごとに【1】ヶ月発生",
            rentChange: "賃貸借契約ごとに現在より【3】%下落",
          },
        ],
      ]),
    []
  );

  const selectedDescription = React.useMemo(() => {
    return simulationConfigDescriptions.get(simulationPattern);
  }, [simulationPattern, simulationConfigDescriptions]);

  return { simulationPattern, onClickSave, simulationConfigDescriptions, selectedDescription };
};

export const SimulationSettings: React.FC<{ onCustomize: () => void }> = ({ onCustomize }) => {
  const classes = useStyles();
  const { simulationPattern, onClickSave, simulationConfigDescriptions } = useSimulationSettings();
  return (
    <Box className={classes.root}>
      <Typography component="h2" className={classes.title}>
        シミュレーション設定
      </Typography>
      <Box role="radiogroup" aria-label="シミュレーション設定">
        {SimulationConfigOptions.slice(0, 3).map(({ value, label }) => {
          const description = simulationConfigDescriptions.get(value);
          return (
            <ButtonBase
              key={value}
              className={classes.choice}
              role="radio"
              aria-checked={simulationPattern === value}
              onClick={() => onClickSave(value)}
            >
              <Box>
                <Typography className={classes.label}>{label}</Typography>
                <Typography className={classes.description}>
                  物件価格：{description?.price}
                  <br />
                  空室期間：{description?.vacancyPeriod}
                  <br />
                  家賃変動：{description?.rentChange}
                </Typography>
              </Box>
              <CheckCircleOutlineIcon
                style={{ color: simulationPattern === value ? colors["action/primary"] : colors["icon/muted"] }}
              />
            </ButtonBase>
          );
        })}
      </Box>
      <Box mt={3}>
        <DetailLink
          title="カスタム設定"
          description={
            simulationPattern === SimulationConfigOptionCustom
              ? "自分で設定した条件を使用中"
              : "変動率や空室期間を自分で設定"
          }
          onClick={() => {
            onClickSave(SimulationConfigOptionCustom);
            onCustomize();
          }}
        />
      </Box>
    </Box>
  );
};
